import React, { useCallback, useEffect, useContext, useMemo, useRef, useState } from 'react'

import { isFunction, isUndefined, debounce } from 'lodash'

import { OverlaySettings } from './OverlaySettings'
import { ColorsPalette } from '../../../../config/colors'
import { floatRound, getNextOrNull, isEqual, isNonEmptyArray, safeSpread } from '../../../../utils'
import SwimLane from './SwimLane'
import WeekNumberWithTooltip from '../../Tooltips/WeekTooltip'
import { cssMax, px, valueFromCss } from '../../../../utils/cssHelper'
import { useDimensions } from '../../../../utils/hooks/useDimensions'
import { Keyboard } from '../../../../utils/constants'
import { Events, newSelection } from '../../../../utils/events'
import { useKeyboardEvents, KeyboardEvents } from '../../../../utils/hooks/useKeyboardEvents'
import { DEFAULT_AREA, HighlightedColumnsContext } from '../../../../Providers/Quotas/HighlightedColumnsProvider'
import { getSwimlaneHeight } from '../utils'

import '../../../../assets/drag-table.scss'

export const DEFAULT_SELECTED_VALUES = { values: [], startDate: null }

export const CONFIG = {
  cell: { style: { width: '50px', minWidth: '25px', height: '25px' }, keyboard: { canMove: true } },
  title: { style: { textAlign: 'center', minWidth: '150px' } },
  titleCell: { style: { height: '50px', minWidth: '150px' } },
  offset: { left: 3, top: 1 },
  overlay: new OverlaySettings()
}

const DEFAULT_EVENTS = Object.values(KeyboardEvents)
const KEYS = [Keyboard.ESCAPE, Keyboard.DELETE, Keyboard.ARROW_DOWN, Keyboard.ARROW_LEFT, Keyboard.ARROW_RIGHT, Keyboard.ARROW_UP]

const DragTable = ({
  columns = [], config = CONFIG, data = [], firstLaneColor, highlightedColumns = [], overflows = {}, lines, selected, shouldDisplayOverflow = true, title = '',
  onEvent, onIllegalDrag, onSelect
}) => {
  const hcContext = useContext(HighlightedColumnsContext)

  const [selectedData, setSelectedData] = useState([])
  const [selectedCell, setSelectedCell] = useState(null)
  const [isPressed, setIsPressed] = useState(false)

  const containerRef = useRef(null)
  const contentRef = useRef(null)
  const headerRef = useRef(null)

  useEffect(() => {
    setSelectedData(data => isEqual(selectedData, selected) ? data : data.filter(d => isNonEmptyArray(selected) && selected.some(s => s.id === d.id)))
  }, [selected])

  useEffect(() => {
    if (isFunction(onSelect)) {
      const callback = debounce(() => onSelect({ values: selectedData, selectedCell: selectedCell }), 100)

      callback()

      return () => {
        callback.cancel()
      }
    }
  }, [onSelect, selectedCell, selectedData])

  const keyboardEvent = useKeyboardEvents({
    eventTypes: useMemo(() => DEFAULT_EVENTS, []),
    enabled: useCallback(event => KEYS.includes(event.key), [])
  })

  const dimensions = useDimensions({ elem: containerRef.current })

  const swimLaneLength = useMemo(() => columns.reduce((acc, c) => acc + c.weeks.length, 0), [columns])

  const titleConf = useMemo(() => config.title ?? CONFIG.title, [config])
  const titleCell = useMemo(() => ({ ...CONFIG.titleCell, ...safeSpread(config.titleCell) }), [config.titleCell])
  const overlay = useMemo(() => CONFIG.overlay.clone(config.overlay ?? {}), [config.overlay])
  const cellConf = useMemo(() => {
    const cellConf = { ...(config.cell ?? CONFIG.cell) }
    const minValue = valueFromCss(titleConf.style.minWidth)

    if (dimensions.width > 0 && swimLaneLength > 0) {
      cellConf.style = { ...cellConf.style, width: px(floatRound(((dimensions.width - minValue - 1) / swimLaneLength), 2)) }
    }

    if (selectedCell !== null) {
      cellConf.keyboard = { canMove: false }
    }

    return cellConf
  }, [config, dimensions.width, swimLaneLength, selectedCell])
  const displacement = useMemo(() => Math.max(valueFromCss(cellConf.style.minWidth), valueFromCss(cellConf.style.width)), [cellConf.style])

  const handleEvent = useCallback((event, data, key) => {
    if (data !== null) {
      if (data.x[1] < data.x[0]) {
        data.x[1] = data.x[0]
      }

      if (data.y[1] < data.y[0]) {
        data.y[1] = data.y[0]
      }
    }

    let newSelectedValues = newSelection(selectedData, data, event)
    let newSelectedCell = null

    if (data?.data === null && event !== Events.LONG_CLICK) {
      if (selectedCell === null || selectedCell.key !== key || selectedCell.x[0] !== data.x[0] || selectedCell.x[1] !== data.x[1]) {
        newSelectedCell = { ...data, key }
        newSelectedValues = selectedData
      }
    }

    if ((event === Events.CLICK || event === Events.DRAG_STOP) && !isUndefined(hcContext)) {
      hcContext.setArea(DEFAULT_AREA)
    }

    if (isFunction(onEvent)) {
      onEvent(event, data, key)
    }

    setSelectedCell(newSelectedCell)
    setSelectedData(newSelectedValues)
  }, [selectedData, selectedCell, onEvent])

  useEffect(() => {
    if (keyboardEvent?.key && keyboardEvent?.type === KeyboardEvents.KEYDOWN) {
      if (keyboardEvent.key === Keyboard.DELETE && isNonEmptyArray(selectedData)) {
        handleEvent(Events.DELETE_PRESSED, selectedData[0])
      } else if (keyboardEvent.key === Keyboard.ESCAPE) {
        handleEvent(Events.ESCAPE_PRESSED, null)
      }
    }
  }, [keyboardEvent?.key, keyboardEvent?.type, selectedData, handleEvent])

  useEffect(() => {
    if (
      keyboardEvent?.key &&
      keyboardEvent?.type === KeyboardEvents.KEYDOWN &&
      [Keyboard.ARROW_DOWN, Keyboard.ARROW_LEFT, Keyboard.ARROW_RIGHT, Keyboard.ARROW_UP].includes(keyboardEvent.key)
    ) {
      setSelectedCell(prev => getNextOrNull(prev, keyboardEvent.key, { x: swimLaneLength, y: getSwimlaneHeight(lines, overflows, prev?.key) }))
    }
  }, [keyboardEvent?.key, keyboardEvent?.type, keyboardEvent, swimLaneLength, lines, overflows])

  const monthWidths = useCallback(month => {
    const numberOfWeeks = month.weeks.length

    const cellWidth = valueFromCss(cellConf.style.width, 2)
    const cellMinWidth = valueFromCss(cellConf.style.minWidth)

    return {
      minWidth: px(cellMinWidth * numberOfWeeks),
      width: px(cellWidth * numberOfWeeks),
      maxWidth: cssMax(px(cellWidth * numberOfWeeks), px(cellMinWidth * numberOfWeeks))
    }
  }, [cellConf.style, overlay.displayWeekNumbers])

  const columnsToHighlight = useMemo(() => overlay.displayHighlightedColumns ? highlightedColumns : [], [overlay.displayHighlightedColumns, highlightedColumns])

  const shouldDisplayHeader = useMemo(() => (overlay.displayMonths || overlay.displayWeekNumbers), [overlay.displayMonths, overlay.displayWeekNumbers])

  const handleMouseDown = e => {
    e.preventDefault()
    e.stopPropagation()

    setIsPressed(true)

    const startCell = getXCoordinate(e.clientX)

    if (selectedData.length > 0) {
      setSelectedData([])
      onEvent(Events.CLICK, null)
    }

    hcContext.setArea({ init: startCell, start: startCell, end: startCell })
  }

  const logMouseMovement = e => {
    if (isPressed) {
      const endCell = getXCoordinate(e.clientX)

      if (endCell >= hcContext.area.init && endCell !== hcContext.area.end) {
        hcContext.setArea({ ...hcContext.area, end: endCell })
      } else if (endCell < hcContext.area.init && endCell !== hcContext.area.start) {
        hcContext.setArea({ ...hcContext.area, start: endCell })
      }
    }
  }

  const handleMouseUp = e => {
    setIsPressed(false)
  }

  const getXCoordinate = x => {
    const header = headerRef.current.getBoundingClientRect()

    return Math.floor((x - header.left - valueFromCss(titleConf.style.minWidth)) / displacement)
  }

  const trackingProps = isUndefined(hcContext)
    ? {}
    : {
      onMouseUp: handleMouseUp,
      onMouseDown: handleMouseDown,
      onMouseMove: logMouseMovement,
      onMouseEnter: e => setIsPressed(hcContext.area.init !== null && e.buttons !== 0)
    }

  return (
    <div ref={containerRef} className='draggable-table-container h-100'>
      <div className='table-container w-100'>
        <div ref={contentRef} className='table-content w-100 h-100'>
          <table className='w-mc' style={{ top: 0, position: 'sticky', zIndex: 250 }}>
            {shouldDisplayHeader && (
              <thead style={{ backgroundColor: ColorsPalette.WHITE_SMOKE }}>
                <tr>
                  <th className='drag-table-title month-header' style={{ ...titleConf.style, textAlign: 'center' }} rowSpan='2'> {title} </th>
                  {overlay.displayMonths && columns.map((c, index) => {
                    const height = overlay.displayWeekNumbers ? cellConf.style.height : titleConf.style.height
                    const colSpan = overlay.displayWeekNumbers ? c.weeks.length : 1
                    const widths = monthWidths(c)

                    return (
                      <th className='month-header' key={`col_${index}`} colSpan={colSpan} style={{ ...widths, height }}> {c.label} </th>
                    )
                  })}
                </tr>
                {overlay.displayWeekNumbers && (
                  <tr ref={headerRef} {...trackingProps}>
                    {columns.reduce((acc, col) => {
                      col.weeks.forEach((w, index) => {
                        const height = overlay.displayMonths ? cellConf.style.height : titleConf.style.height
                        const className = (
                          selectedData.some(data => data !== null && data.x[0] <= acc.length && acc.length <= data.x[1]) ||
                          (!isUndefined(hcContext) && hcContext.area.init !== null && hcContext.area.start <= acc.length && acc.length <= hcContext.area.end)
                        ) ? 'highlighted'
                          : ''

                        acc.push(
                          <WeekNumberWithTooltip key={`${w.number}_${index}`} style={{ ...cellConf.style, height }} className={className} week={w} />
                        )
                      })
                      return acc
                    }, [])}
                  </tr>
                )}
              </thead>
            )}
          </table>
          {lines.map(({ id, places, name, tooltip, infos }, index) => {
            const handleLaneEvent = (event, data) => handleEvent(event, data, id)

            return (
              <SwimLane
                firstRowColor={index === 0 ? firstLaneColor : undefined}
                key={id}
                cellConf={cellConf}
                data={data[id]}
                height={places}
                highlightedColumns={columnsToHighlight}
                infos={infos}
                overlay={overlay}
                overflow={overflows[id] ?? 0}
                width={swimLaneLength}
                selectedCell={selectedCell?.key === id ? selectedCell : null}
                selectedData={selectedData}
                title={name}
                titleCell={titleCell}
                tooltip={tooltip}
                zIndex={200 - index}
                shouldDisplayOverflow={shouldDisplayOverflow}
                onEvent={handleLaneEvent}
                onIllegalDrag={onIllegalDrag}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default DragTable
